import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { accessLevel, canManageMasterData } from '@/auth/access';
import UpdateNotice from '@/components/feature/UpdateNotice';

const navItems = [
  { path: '/', label: 'Início', icon: 'ri-home-5-line' },
  { path: '/clientes', label: 'Clientes', icon: 'ri-user-add-line' },
  { path: '/produtos', label: 'Produtos', icon: 'ri-add-box-line' },
  { path: '/usuarios', label: 'Usuários', icon: 'ri-team-line' },
  { path: '/calculadora', label: 'Calculadora', icon: 'ri-calculator-line' },
  { path: '/tabela-precos', label: 'Tabela de Preços', icon: 'ri-price-tag-3-line' },
  { path: '/cotacoes', label: 'Cotações', icon: 'ri-file-list-3-line' },
  { path: '/indicadores', label: 'Indicadores', icon: 'ri-bar-chart-box-line' },
  { path: '/solicitacoes', label: 'Projetos e Amostras', icon: 'ri-flask-line' },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout, isImpersonating, stopAccessAs } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const visibleNavItems = navItems
    .map((item) => item.path === '/usuarios' && accessLevel(user) === 'manager' ? { ...item, label: 'Minha equipe' } : item)
    .filter((item) => {
      if (['/clientes', '/produtos'].includes(item.path)) return canManageMasterData(user);
      if (item.path === '/usuarios') return accessLevel(user) !== 'seller';
      if (item.path === '/solicitacoes') return accessLevel(user) === 'admin';
      return true;
    });

  useEffect(() => { setMobileMenuOpen(false); setMoreMenuOpen(false); }, [location.pathname]);

  const isActive = (path: string) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const morePaths = ['/cotacoes', '/indicadores', '/solicitacoes'];
  const primaryNavItems = visibleNavItems.filter((item) => !morePaths.includes(item.path));
  const moreNavItems = visibleNavItems.filter((item) => morePaths.includes(item.path));

  return (
    <div className="min-h-screen bg-background-50">
      <header className="sticky top-0 z-40 bg-primary-500 text-white shadow-sm">
        {isImpersonating && <div className="flex flex-wrap items-center justify-center gap-3 bg-amber-100 px-4 py-3 text-sm text-amber-950" role="status">
          <span><i className="ri-eye-line mr-2" />Simulando acesso: <strong>{user?.name}</strong> · {user?.role}</span>
          <button type="button" onClick={() => { stopAccessAs(); navigate('/usuarios', { replace: true }); }} className="rounded-lg border border-amber-500 bg-white px-3 py-1.5 font-bold">Voltar ao administrador</button>
        </div>}
        <div className="h-16 px-4 lg:px-5 flex items-center gap-3 max-w-[1920px] mx-auto">
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0" aria-label="Ir para o início">
            <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center p-1">
              <img src="/brand/zschimmer-mark.png" alt="Zschimmer & Schwarz" className="w-full h-full object-contain" />
            </div>
            <div className="hidden 2xl:block leading-tight">
              <p className="text-[10px] font-bold uppercase tracking-wider">ZSCHIMMER & SCHWARZ</p>
              <p className="text-[10px] text-white/65">BRASIL</p>
            </div>
          </Link>

          <nav className="hidden lg:flex flex-1 min-w-0 items-center gap-1 py-2">
            {primaryNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex min-w-0 items-center gap-2 px-2.5 xl:px-3 py-2 rounded-xl text-[13px] xl:text-sm font-medium transition-all ${isActive(item.path) ? 'bg-white text-primary-700 shadow-sm' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}
              >
                <i className={`${item.icon} text-base`} />
                <span className="hidden xl:inline whitespace-nowrap">{item.label}</span>
              </Link>
            ))}
            {moreNavItems.length > 0 && <div className="relative">
              <button type="button" onClick={() => setMoreMenuOpen((open) => !open)} className={`flex items-center gap-2 px-2.5 xl:px-3 py-2 rounded-xl text-[13px] xl:text-sm font-medium transition-all ${moreNavItems.some((item) => isActive(item.path)) ? 'bg-white text-primary-700 shadow-sm' : 'text-white/75 hover:bg-white/10 hover:text-white'}`} aria-expanded={moreMenuOpen}><i className="ri-apps-2-line text-base" /><span className="hidden xl:inline">Mais</span><i className="ri-arrow-down-s-line" /></button>
              {moreMenuOpen && <div className="absolute left-0 top-full mt-2 w-56 rounded-xl border border-background-200 bg-white p-1.5 text-foreground-900 shadow-xl">
                {moreNavItems.map((item) => <Link key={item.path} to={item.path} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${isActive(item.path) ? 'bg-primary-50 text-primary-800 font-semibold' : 'hover:bg-background-50'}`}><span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-700"><i className={item.icon} /></span>{item.label}</Link>)}
              </div>}
            </div>}
          </nav>

          <div className="hidden lg:flex items-center gap-2 flex-shrink-0 border-l border-white/20 pl-3">
            <Link to="/perfil" className={`flex items-center gap-2 rounded-xl p-1 pr-2 transition-colors hover:bg-white/10 ${isActive('/perfil') ? 'bg-white/15' : ''}`} title="Abrir meu perfil">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-xs font-bold">{user?.name?.charAt(0) || 'U'}</div>
              <div className="hidden min-[1750px]:block max-w-32 leading-tight">
                <p className="text-sm font-semibold truncate">{user?.name}</p>
                <p className="text-[11px] text-white/65 truncate">{user?.role}</p>
              </div>
            </Link>
            <button type="button" onClick={logout} className="h-9 w-9 min-[1750px]:w-auto min-[1750px]:px-3 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors" title="Sair">
              <i className="ri-logout-box-r-line" /> <span className="hidden min-[1750px]:inline ml-1">Sair</span>
            </button>
          </div>

          <button type="button" onClick={() => setMobileMenuOpen((open) => !open)} className="lg:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-lg bg-white/10" aria-label="Abrir menu">
            <i className={mobileMenuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-white text-foreground-900 border-t border-background-200 shadow-lg p-3">
            <nav className="grid sm:grid-cols-2 gap-1">
              <Link to="/perfil" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive('/perfil') ? 'bg-primary-500 text-white' : 'bg-background-50 hover:bg-background-100'}`}><span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-bold">{user?.name?.charAt(0) || 'U'}</span><span><span className="block font-semibold">{user?.name}</span><span className="block text-[10px] opacity-70">Meu perfil</span></span></Link>
              {visibleNavItems.map((item) => (
                <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive(item.path) ? 'bg-primary-500 text-white' : 'hover:bg-background-100'}`}>
                  <i className={item.icon} />{item.label}
                </Link>
              ))}
              <button type="button" onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50">
                <i className="ri-logout-box-r-line" />Sair
              </button>
            </nav>
          </div>
        )}
      </header>

      <main key={location.pathname} className="route-page-enter w-full min-w-0 overflow-x-hidden">{children}</main>
      <UpdateNotice />
    </div>
  );
}
